import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Zap, Plus, Trash2, Trophy, X, Loader2, BarChart3,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrafficBar({ weight, color = 'bg-stone-800' }) {
  return (
    <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-300`}
        style={{ width: `${weight}%` }}
      />
    </div>
  );
}

function VariantCard({ variant, isOriginal, isWinner, winnerMargin, onAdjust, onDelete, onSelect }) {
  const rate = variant.conversion_rate || 0;
  const views = variant.views || 0;
  const conversions = variant.conversions || 0;
  const weight = variant.variant_weight || 50;

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        isWinner ? 'border-green-200 bg-green-50/50' : 'border-stone-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isWinner && <Trophy className="w-3.5 h-3.5 text-amber-500" />}
          <span className="text-xs font-semibold text-stone-800">
            {isOriginal ? 'Original (Control)' : (variant.variant_name || 'Variant')}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!isOriginal && (
            <button
              onClick={() => onDelete(variant.page_id)}
              className="p-1 text-stone-400 hover:text-red-500 transition-colors"
              title="Delete variant"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Traffic bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-stone-500">Traffic</span>
          <span className="text-[10px] font-semibold text-stone-700">{weight}%</span>
        </div>
        <TrafficBar weight={weight} color={isOriginal ? 'bg-stone-700' : 'bg-blue-500'} />
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 text-[10px] text-stone-500">
        <span>Views: <span className="font-medium text-stone-700">{views.toLocaleString()}</span></span>
        <span>Conv: <span className="font-medium text-stone-700">{conversions.toLocaleString()}</span></span>
        <span>Rate: <span className="font-medium text-stone-700">{rate}%</span></span>
      </div>

      {isWinner && winnerMargin > 0 && (
        <div className="mt-2 text-[10px] font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
          Winner (+{winnerMargin}% conversion rate)
        </div>
      )}

      {!isOriginal && (
        <button
          onClick={() => onSelect(variant.page_id)}
          className="mt-2 w-full text-[10px] text-center py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
        >
          Edit variant blocks
        </button>
      )}
    </div>
  );
}

export default function ABTestPanel({ siteId, pageId, page, pages, getAuthHeader, onPagesUpdate }) {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showSplitSlider, setShowSplitSlider] = useState(false);
  const [sliderVariantId, setSliderVariantId] = useState(null);
  const [sliderValue, setSliderValue] = useState(50);

  const fetchVariants = useCallback(async () => {
    if (!siteId || !pageId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/sites/${siteId}/pages/${pageId}/variants`,
        { headers: getAuthHeader() }
      );
      setVariants(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // No variants yet is fine
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, pageId, getAuthHeader]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const variantCount = variants.filter(v => !v.is_original).length;
      const name = `Variant ${String.fromCharCode(66 + variantCount)}`; // B, C, D...
      await axios.post(
        `${API}/sites/${siteId}/pages/${pageId}/variants`,
        { variant_name: name, variant_weight: 50 },
        { headers: getAuthHeader() }
      );
      toast.success(`${name} created`);
      await fetchVariants();
      if (onPagesUpdate) {
        const pagesRes = await axios.get(`${API}/sites/${siteId}/pages`, { headers: getAuthHeader() });
        onPagesUpdate(Array.isArray(pagesRes.data) ? pagesRes.data : pagesRes.data?.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create variant');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (variantId) => {
    if (!window.confirm('Delete this variant? This cannot be undone.')) return;
    try {
      await axios.delete(
        `${API}/sites/${siteId}/pages/${pageId}/variants/${variantId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Variant deleted');
      await fetchVariants();
      if (onPagesUpdate) {
        const pagesRes = await axios.get(`${API}/sites/${siteId}/pages`, { headers: getAuthHeader() });
        onPagesUpdate(Array.isArray(pagesRes.data) ? pagesRes.data : pagesRes.data?.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete variant');
    }
  };

  const handleAdjustSplit = (variantId) => {
    const v = variants.find(x => x.page_id === variantId);
    setSliderVariantId(variantId);
    setSliderValue(v?.variant_weight || 50);
    setShowSplitSlider(true);
  };

  const handleSaveSplit = async () => {
    if (!sliderVariantId) return;
    try {
      await axios.put(
        `${API}/sites/${siteId}/pages/${pageId}/variants/${sliderVariantId}/weight`,
        { weight: sliderValue },
        { headers: getAuthHeader() }
      );
      toast.success('Traffic split updated');
      setShowSplitSlider(false);
      await fetchVariants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update split');
    }
  };

  const handleEndTest = async () => {
    // Apply winner: find the variant with highest conversion rate, keep it, delete others
    const winner = variants.reduce((best, v) => {
      return (v.conversion_rate || 0) > (best.conversion_rate || 0) ? v : best;
    }, variants[0]);

    if (!winner) return;

    const action = window.confirm(
      `End A/B test and keep "${winner.is_original ? 'Original' : winner.variant_name}" as the winner?\n\nAll other variants will be deleted.`
    );
    if (!action) return;

    try {
      // If winner is a variant, we could copy its blocks to original (for now just delete losers)
      const losers = variants.filter(v => v.page_id !== winner.page_id && !v.is_original);
      for (const loser of losers) {
        await axios.delete(
          `${API}/sites/${siteId}/pages/${pageId}/variants/${loser.page_id}`,
          { headers: getAuthHeader() }
        );
      }
      toast.success('A/B test ended');
      await fetchVariants();
      if (onPagesUpdate) {
        const pagesRes = await axios.get(`${API}/sites/${siteId}/pages`, { headers: getAuthHeader() });
        onPagesUpdate(Array.isArray(pagesRes.data) ? pagesRes.data : pagesRes.data?.data || []);
      }
    } catch (err) {
      toast.error('Failed to end test');
    }
  };

  const handleSelectVariant = (variantPageId) => {
    // Navigate to the variant page in the builder
    if (onPagesUpdate) {
      // The parent SiteBuilder can use this to switch to the variant page
      window.dispatchEvent(new CustomEvent('switch-page', { detail: { pageId: variantPageId } }));
    }
  };

  const hasVariants = variants.filter(v => !v.is_original).length > 0;

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-l border-stone-200 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            A/B Test
          </h3>
        </div>
        {hasVariants && (
          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
            Active
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Current page info */}
        <p className="text-[10px] text-stone-400">
          Testing: <span className="font-medium text-stone-600">{page?.name || 'Current page'}</span>
        </p>

        {/* Variant cards */}
        {variants.map((v) => (
          <VariantCard
            key={v.page_id}
            variant={v}
            isOriginal={v.is_original}
            isWinner={v.is_winner}
            winnerMargin={v.winner_margin}
            onAdjust={handleAdjustSplit}
            onDelete={handleDelete}
            onSelect={handleSelectVariant}
          />
        ))}

        {/* No variants message */}
        {!hasVariants && (
          <div className="text-center py-6">
            <BarChart3 className="w-8 h-8 text-stone-300 mx-auto mb-2" />
            <p className="text-xs text-stone-500 mb-1">No A/B test running</p>
            <p className="text-[10px] text-stone-400">
              Create a variant to test different versions of this page
            </p>
          </div>
        )}

        {/* Split slider */}
        {showSplitSlider && (
          <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-stone-700">Adjust Traffic Split</span>
              <button onClick={() => setShowSplitSlider(false)} className="p-0.5 text-stone-400 hover:text-stone-600">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div>
              <div className="flex justify-between text-[10px] text-stone-500 mb-1">
                <span>Original: {100 - sliderValue}%</span>
                <span>Variant: {sliderValue}%</span>
              </div>
              <input
                type="range"
                min={10}
                max={90}
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                className="w-full accent-stone-800"
              />
            </div>
            <Button size="sm" onClick={handleSaveSplit} className="w-full text-xs bg-stone-800 hover:bg-stone-900 text-white">
              Save Split
            </Button>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-stone-200 space-y-2 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={handleCreate}
          disabled={creating}
          className="w-full text-xs"
        >
          {creating ? (
            <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Creating...</>
          ) : (
            <><Plus className="w-3 h-3 mr-1" /> Add Variant</>
          )}
        </Button>

        {hasVariants && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const firstVariant = variants.find(v => !v.is_original);
                if (firstVariant) handleAdjustSplit(firstVariant.page_id);
              }}
              className="w-full text-xs"
            >
              Adjust Split
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEndTest}
              className="w-full text-xs text-red-600 border-red-200 hover:bg-red-50"
            >
              End Test
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
